import { setCookie } from '../../utils/cookies';

/**
 * Конфигурация A/B теста
 */
interface ABConfig {
  /**
   * Уникальный идентификатор теста
   */
  testId: string;
}

/**
 * Класс для управления A/B тестированием с гарантированным распределением 50/50
 *
 * @description
 * Реализует механизм A/B тестирования на основе счетчика для обеспечения
 * равномерного распределения пользователей между вариантами 'a' и 'b'.
 *
 * Особенности:
 * - Гарантирует распределение 50/50 через инкрементальный счетчик
 * - Сохраняет назначенный вариант в cookies для постоянства
 * - Использует localStorage для хранения счетчика
 *
 * @example
 * // Создание экземпляра для конкретного теста
 * const abTest = new ABTesting('button_color_test_2024');
 *
 * // Получение варианта для текущего пользователя
 * const variant = abTest.getVariant(); // 'a' или 'b'
 *
 * // Повторный вызов вернет тот же вариант из cookies
 * const sameVariant = abTest.getVariant(); // тот же вариант
 */
export class ABTesting {
  private config: ABConfig;
  private abTestingCounterKey: string;

  /**
   * Создает новый экземпляр A/B теста
   *
   * @param testId - Уникальный идентификатор теста
   */
  constructor(testId: string) {
    this.config = {
      testId: testId,
    };

    this.abTestingCounterKey = `ab_counter_${this.config.testId}`;
  }

  /**
   * Получает вариант теста для пользователя
   *
   * @returns Вариант теста: 'a' или 'b'
   */
  getVariant(): 'a' | 'b' {
    // Сначала проверяем, есть ли уже назначенный вариант
    const existingVariant = this.getCookie();

    if (existingVariant === 'a' || existingVariant === 'b') {
      return existingVariant;
    }

    // Назначаем новый вариант по счётчику
    const newVariant = this.assignVariantByCounter();

    this.setCookie(newVariant);

    return newVariant;
  }

  /**
   * Назначает вариант теста на основе счетчика
   *
   * @returns Вариант теста: 'a' для четных значений счетчика, 'b' для нечетных
   */
  private assignVariantByCounter(): 'a' | 'b' {
    const counter = this.getCounter();

    // Если счетчик не инициализирован (первый пользователь), выбираем случайный вариант 50/50
    if (counter === null) {
      const randomVariant = Math.random() < 0.5 ? 'a' : 'b';

      // Инициализируем счетчик случайным значением (0 или 1) для последующих пользователей
      const initialCounter = randomVariant === 'a' ? 0 : 1;

      this.setCounter(initialCounter + 1);

      return randomVariant;
    }

    const variant = counter % 2 === 0 ? 'a' : 'b';

    this.setCounter(counter + 1);

    return variant;
  }

  /**
   * Получает текущее значение счетчика из localStorage
   *
   * @returns Текущее значение счетчика, или null если счетчик не инициализирован
   */
  private getCounter(): number | null {
    const stored = localStorage.getItem(this.abTestingCounterKey);

    return stored ? parseInt(stored, 10) : null;
  }

  /**
   * Сохраняет новое значение счетчика в localStorage
   *
   * @param value - Новое значение счетчика
   */
  private setCounter(value: number): void {
    localStorage.setItem(this.abTestingCounterKey, value.toString());
  }

  /**
   * Получает сохраненный вариант теста из cookies
   *
   * @returns Вариант теста из cookies или null, если не найден
   */
  private getCookie(): string | null {
    const name = `ab_${this.config.testId}=`;
    const cookies = document.cookie.split(';');

    for (const cookie of cookies) {
      const trimmed = cookie.trim();

      if (trimmed.startsWith(name)) {
        return trimmed.substring(name.length);
      }
    }

    return null;
  }

  /**
   * Сохраняет назначенный вариант в cookies
   *
   * @param variant - Вариант теста для сохранения ('a' или 'b')
   */
  private setCookie(variant: 'a' | 'b'): void {
    setCookie(`ab_${this.config.testId}`, variant);
  }
}
